import {
  Driver,
  RouteOptimizationObjectiveType,
  RoutingJobCreate,
  RoutingVehicleCreate,
  RoutingWorkflowType,
  Stop,
} from "@api/graphql/generated/generated-types";

function unique(values: (string | null | undefined)[]): string[] {
  return Array.from(
    new Set(values.filter((value): value is string => !!value))
  );
}

// Dispatch is per order, not per stop: these are the orders that will move.
export function getTransferShipmentIds(stops: Stop[]): string[] {
  return unique(stops.map((stop) => stop.shipmentId));
}

export function getTransferOrderIds(stops: Stop[]): string[] {
  return unique(stops.map((stop) => stop.shipment?.order?.id));
}

// Dates of the stops that were actually checked. The routing job moves whole
// orders though, so these alone are not enough: useTransferStopDates resolves
// the full set before the job is created.
export function getSelectedStopDates(
  stops: Stop[],
  fallbackDate: string
): string[] {
  const dates = unique(
    stops.map((stop) => stop.effectiveStopDate || stop.stopDate)
  );
  return dates.length > 0 ? dates : [fallbackDate];
}

interface TransferJobCreateParams {
  courierId: string;
  createdBy: string;
  shipmentIds: string[];
}

export function buildTransferJobCreate({
  courierId,
  createdBy,
  shipmentIds,
}: TransferJobCreateParams): RoutingJobCreate {
  return {
    courierId,
    createdBy,
    earlyPenaltyLevel: 0,
    latePenaltyLevel: 0,
    shipmentIds,
    // On-time first so a pickup isn't sequenced before it's ready (BLU-174).
    objectiveType: RouteOptimizationObjectiveType.OnTime,
    respectReadyTimeAsDeparture: true,
    // Slot the moved stops into the target driver's route without reshuffling
    // the stops already on it.
    preserveExistingRouteOrder: true,
    workflowType: RoutingWorkflowType.QuickDispatchWithMinimalOptimization,
  };
}

// The job is rejected for a node/vehicle date mismatch unless the target
// driver has a vehicle on every date the moved stops land on.
export function buildTransferVehicleCreates(
  driver: Driver,
  stopDates: string[]
): RoutingVehicleCreate[] {
  return stopDates.map((date) => ({
    date,
    driverId: driver.id,
    driverSpeed: driver.speed ?? 1.0,
    useCurrentLocationIdAtStart: true,
  }));
}

export function pluralizeOrders(count: number): string {
  return `${count} ${count === 1 ? "order" : "orders"}`;
}
